import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import CreditDisplayCard from '@/components/Credits/CreditDisplayCard';
import { useAuthStore } from '@/store';
import { formatCreditBalance } from '@/lib/formatCredits';

const createStyles = (colors) => StyleSheet.create({
  sectionContainer: {
    marginBottom: 30,
    width: '100%',
  },
  sectionHeader: {
    marginBottom: 15,
  },
  sectionSubtitle: {
    marginVertical: 5,
  },
  creditStats: {
    gap: 15,
  },
  creditRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 2,
  },
  progressBar: {
    height: 10,
    backgroundColor: colors.solid_color_secondary_dark,
    borderRadius: 10,
    marginTop: 15,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 10,
  },
  resetDate: {
    marginTop: 15,
    textAlign: 'left',
  },
});

// Format reset date
const formatResetDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

function MonthlyCreditsSection({ total, used, remaining, resetDate }) {
  const { colors } = useTheme();
  const isPlusPremium = useAuthStore(state => state.user?.flags?.isPlusPremium);
  const styles = useMemo(() => createStyles(colors), [colors]);

  // Calculate progress percentage for monthly credits
  const usagePercentage = (used / total) * 100;
  const remainingCredits = formatCreditBalance(remaining);
  const usedCredits = formatCreditBalance(used);
  const totalCredits = formatCreditBalance(total);
  const resetDateFormatted = resetDate ? formatResetDate(resetDate) : 'Unknown';

  // Get progress bar color based on usage
  const getProgressColor = useCallback((percentage) => {
    if (percentage < 50) return colors.accent_color_primary;
    if (percentage < 80) return '#F59E0B'; // Warning color
    return colors.color_destructive;
  }, [colors]);

  return (
    <View style={styles.sectionContainer}>
      <View style={styles.sectionHeader}>
        <Text color="primary" size='xl' weight='bold'>
          Monthly Credit Allotment
        </Text>
        <Text style={styles.sectionSubtitle} size="n" color="secondary">
          {`Included in your Gab AI ${isPlusPremium ? 'Plus Premium' : 'Plus'} subscription`}
        </Text>
      </View>
      
      <CreditDisplayCard 
        credits={remainingCredits}
        icon="sparkles"
        text="Credits remaining this month"
      />
        
      <View style={styles.creditStats}>
        <View style={styles.creditRow}>
          <Text size="n" color="secondary">Usage this month</Text>
          <Text size="n" weight="medium" color="primary">
            {usedCredits} of {totalCredits}
          </Text>
        </View>
      </View>

      <View style={styles.progressBar}>
        <View 
          style={[
            styles.progressFill, 
            { 
              width: `${Math.min(usagePercentage, 100)}%`,
              backgroundColor: getProgressColor(usagePercentage)
            }
          ]} 
        />
      </View>

      <Text size="xs" color="tertiary" style={styles.resetDate}>
        Resets on {resetDateFormatted}
      </Text>
    </View>
  );
} 

export default React.memo(MonthlyCreditsSection);